import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';
import Badge from './Badge';

interface FacingDirectionBadgeProps {
  direction: 'left' | 'right';
  confidence: number;
  isAutoApplied?: boolean;
  className?: string;
  showConfidence?: boolean;
}

/**
 * Badge component to display detected facing direction with confidence
 *
 * Visual indicators:
 * - Green badge: High confidence (> 85%) - auto-applied
 * - Yellow badge: Medium confidence (50-85%) - needs confirmation
 * - Shows direction arrow and optional confidence percentage
 *
 * @example
 * ```tsx
 * <FacingDirectionBadge
 *   direction="left"
 *   confidence={0.92}
 *   isAutoApplied={true}
 *   showConfidence={true}
 * />
 * ```
 */
function FacingDirectionBadge({
  direction,
  confidence,
  isAutoApplied = false,
  className,
  showConfidence = true,
}: FacingDirectionBadgeProps) {
  // Determine badge variant based on confidence
  const variant = confidence >= 0.85 ? 'success' : 'warning';

  // Direction icon
  const DirectionIcon = direction === 'left' ? ArrowLeftIcon : ArrowRightIcon;

  // Direction label
  const directionLabel = direction === 'left' ? 'Left' : 'Right';

  // Format confidence percentage
  const confidenceText = `${Math.round(confidence * 100)}%`;

  return (
    <Badge
      variant={variant}
      className={cn('flex items-center gap-1.5', className)}
      size="md"
    >
      <DirectionIcon className="w-4 h-4" aria-hidden="true" />
      <span className="font-medium">{directionLabel}</span>
      {showConfidence && (
        <>
          <span className="text-xs opacity-70">·</span>
          <span className="text-xs opacity-90">{confidenceText}</span>
        </>
      )}
      {isAutoApplied && confidence >= 0.85 && (
        <span className="text-xs ml-1 opacity-70">(auto)</span>
      )}
    </Badge>
  );
}

export default FacingDirectionBadge;
